import { reducerWithInitialState } from 'typescript-fsa-reducers'

/* Import our module's actions */
import * as a from './actions'
import * as aa from 'modules/auth/actions'
import produce from 'immer'

/**
 * Export the StoreState interface for this module. We always name this interface
 * `StoreState` so it is consistent across each of our modules.
 * We export a readonly version of the interface, to try to prevent modifications,
 * and retain a private (not exported) MutableStoreState for ourselves to use very
 * carefully in the reducers, if we need to.
 */
export type StoreState = DeepReadonly<MutableStoreState>

interface MutableStoreState {
	/** The uid of the user who owns the projects */
	userUid?: string
	/**
	 * Whether the value in userUid is valid or not. This is to help with migration from older versions that did not have
	 * the userUid property, so an undefined userUid does not necessarily mean no user, it just means we didn't record it.
	 */
	userUidIsValid?: boolean

	online: boolean

	/** Whether we are actively processing downloaded sync data */
	syncing: boolean
	/** The number of projects processed by the sync in progress */
	syncedItems: number
	/** The number of projects the sync in progress has found to download */
	itemsToSync: number
}

/**
 * The initial store state for this module.
 */
const INITIAL_STATE: StoreState = {
	/* Note that we end each property with a comma, so we can add new properties without modifying this line
	(improve your git diffs!).
	 */
	online: false,
	syncing: false,
	syncedItems: 0,
	itemsToSync: 0,
}

/**
 * Reducer function for this module.
 */
export const reducer = reducerWithInitialState(INITIAL_STATE)

reducer.case(aa.login, (state) => produce(state, draft => {
	if (!draft.userUidIsValid) {
		/* If the user requests to sign in, then we can now say that the userUid field is valid as blank,
		   and therefore we'll blank the projects when they login successfully, as there was no user logged in.
		 */
		draft.userUidIsValid = true
		draft.userUid = undefined
	}
}))

reducer.case(aa.loggedIn, (state, payload) => produce(state, draft => {
	if (draft.userUid !== payload.uid) {
		if (!draft.userUidIsValid) {
			/* The user gets logged in automatically when the app launches if Firebase has remembered them,
			   so if we haven't seen an explicit login request, we can now say that we trust that the logged in
			   user is the user who was logged in before, and therefore owns the projects.
			 */
			draft.userUidIsValid = true
		}
	}
	draft.userUid = payload.uid
}))

reducer.case(a.databaseReady, (state): StoreState => produce(state, draft => {

	/* Remove the legacy projects collection */
	delete (draft as any).projects

	/* Reset any sync state rehydrated from a previous session that was interrupted mid-sync */
	draft.syncing = false
	draft.syncedItems = 0
	draft.itemsToSync = 0
}))

reducer.case(a.onlineStateChange, (state, payload) => produce(state, draft => {
	draft.online = payload
}))

reducer.case(a.syncing, (state, payload) => produce(state, draft => {
	draft.syncing = payload
	if (!payload) {
		draft.syncedItems = 0
		draft.itemsToSync = 0
	}
}))

/* Each snapshot reports what it found before it starts downloading, and a sync can span several snapshots,
   so the counts accumulate until the sync finishes rather than being replaced.
 */
reducer.case(a.syncFound, (state, payload) => produce(state, draft => {
	draft.itemsToSync += payload
}))

reducer.case(a.syncProgress, (state, payload) => produce(state, draft => {
	draft.syncedItems += payload
}))
